<template>
    <div class="gmail-layout">
        <!-- Three-column layout -->
        <div class="gmail-columns" :class="{'supplier-layout': isSupplierUser}">
            <!-- Left column: Companies list (hidden for Supplier users) -->
            <div v-if="!isSupplierUser" class="gmail-column companies-column">
                <company-list
                    :title="$t('messages.hotel_companies')"
                    :companies="companies"
                    :is-loading="isLoadingCompanies"
                    :error="companiesError"
                    :selected-company-id="selectedCompany?.id"
                    @company-selected="selectCompany"
                />
            </div>

            <!-- Middle column: Tickets list -->
            <div class="gmail-column tickets-column" :class="{'supplier-tickets-column': isSupplierUser}">
                <ticket-list
                    :tickets="tickets"
                    :is-loading="isLoadingTickets"
                    :error="ticketsError"
                    :selected-company-id="selectedCompany?.id"
                    :selected-ticket-id="selectedTicket?.id"
                    :show-create-button="isTO1User"
                    :can-create-ticket="hasCompanyRespondents"
                    :loading-progress="loadingProgress"
                    :should-highlight-ticket="shouldHighlightTicket"
                    @ticket-selected="selectTicket"
                    @create-ticket="showNewTicketModal"
                    @refresh-tickets="refreshTickets"
                />
            </div>

            <!-- Right column: Ticket Chat -->
            <div class="gmail-column chat-column">
                <ticket-chat
                    ref="ticketChat"
                    :ticket="selectedTicket"
                    @ticket-updated="onTicketUpdated"
                    @message-sent="onMessageSent"
                />
            </div>
        </div>

        <!-- New Ticket Modal - Only for TO1 users -->
        <new-ticket-modal
            v-if="isTO1User"
            v-model="showTicketModal"
            :company-id="selectedCompany?.id"
            @ticket-created="onTicketCreated"
            @error="onTicketError"
        />

        <!-- Snackbar for notifications -->
        <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="3000">
            {{ snackbarText }}
        </v-snackbar>
    </div>
</template>

<script lang="ts">
    import {Component, Prop, Vue, Watch} from 'vue-facing-decorator'
    import {authStore} from '@/composables/store-accessor'
    import CompanyList from './CompanyList.vue'
    import TicketList from './TicketList.vue'
    import TicketChat from './TicketChat.vue'
    import NewTicketModal from './NewTicketModal.vue'
    import {format} from 'date-fns'
    import {maskSupportName, maskSupportNameInText} from '../../utils/messaging-utils'

    interface Company {
        id: string
        name: string
        company_id?: string
    }

    interface LastMessage {
        senderId: string
        senderName: string
        isTO1Role: boolean
        timestamp: number
        senderExternalId?: string
    }

    interface Ticket {
        id: string
        subject: string
        status: string
        createdAt: number
        updatedAt: number
        companyId: string
        lastMessage?: LastMessage
        cachedMessages?: Message[]
    }

    interface Message {
        id: string
        text: string
        sender: string
        timestamp: number
        isFromUser: boolean
        senderId: string
        senderExternalId?: string
        isTicketAuthor?: boolean
    }

    @Component({
        components: {
            CompanyList,
            TicketList,
            TicketChat,
            NewTicketModal,
        },
    })
    export default class HotellierMessaging extends Vue {
        // Ticket type IDs passed from parent
        @Prop({default: () => []}) hotelierTicketTypeIds!: string[]

        // Companies data
        companies: Company[] = []
        selectedCompany: Company | null = null
        isLoadingCompanies: boolean = false
        companiesError: string | null = null

        // Tickets data
        tickets: Ticket[] = []
        selectedTicket: Ticket | null = null
        isLoadingTickets: boolean = false
        ticketsError: string | null = null
        hasCompanyRespondents: boolean = false
        loadingProgress: string = ''
        showTicketModal: boolean = false

        // Notification data
        showSnackbar: boolean = false
        snackbarText: string = ''
        snackbarColor: string = 'success'

        // User role cache for external IDs
        userRoleCache: Map<string, string> = new Map()

        // Computed property to check if the current user has TO1 role
        get isTO1User(): boolean {
            return authStore.isTO1
        }

        // Computed property to check if the current user has Supplier role
        get isSupplierUser(): boolean {
            return authStore.isSupplier
        }

        // Utility function to check if a role is TO1
        isUserTO1Role(role?: string): boolean {
            if (!role) return false
            return (
                role.includes('to1') ||
                role.includes('TO1') ||
                role === 'director' ||
                role === 'supervisor' ||
                role === 'support'
            )
        }

        // Utility function to determine if a ticket should be highlighted
        shouldHighlightTicket(ticket: Ticket): boolean {
            if (!ticket.lastMessage) {
                return false
            }

            const shouldHighlight = (() => {
                if (this.isTO1User) {
                    // For TO1 users: highlight if last message is NOT from TO1 role
                    return !ticket.lastMessage.isTO1Role
                } else if (this.isSupplierUser) {
                    // For supplier users: highlight if last message is NOT from the supplier themselves
                    // Check if the last message sender is the current user
                    const isFromCurrentUser =
                        ticket.lastMessage.senderId === authStore.person?.personId ||
                        ticket.lastMessage.senderExternalId === authStore.person?.personId?.toString()
                    return !isFromCurrentUser
                }
                return false
            })()

            if (this.isSupplierUser) {
                const isFromCurrentUser =
                    ticket.lastMessage.senderId === authStore.person?.personId ||
                    ticket.lastMessage.senderExternalId === authStore.person?.personId?.toString()
            }

            return shouldHighlight
        }

        mounted() {

            // Load companies
            this.loadCompanies()
        }

        @Watch('showTicketModal')
        onShowTicketModalChange(newVal: boolean, oldVal: boolean) {
        }

        async loadCompanies() {
            this.isLoadingCompanies = true
            this.companiesError = null

            try {
                const token = useCookie('token').value

                if (!token) {
                    throw new Error('Authentication token not found. Please log in.')
                }

                // Use the proxy endpoint that filters companies based on user role
                let response
                try {
                    response = await fetch('/proxy/intercom/companies/filtered-list', {
                        headers: {
                            Accept: 'application/json',
                            Token: token,
                        },
                    })
                } catch (error) {
                    console.error('Error fetching filtered companies list:', error)
                    // If the filtered endpoint fails, fall back to the regular companies list endpoint
                    response = await fetch('/proxy/intercom/companies/list', {
                        headers: {
                            Accept: 'application/json',
                            Token: token,
                        },
                    })
                }

                if (!response.ok) {
                    throw new Error(`Error: ${response.status} - ${response.statusText}`)
                }

                const data = await response.json()

                if (data && Array.isArray(data.companies)) {
                    // Map the companies to our format
                    let companies = data.companies.map((company: any) => ({
                        id: company.id,
                        name: company.name,
                        company_id: company.company_id,
                    }))

                    // If we're not a TO1 user, the server MUST have applied company
                    // filtering (filteringApplied === true). Fail closed: if the
                    // response is not confirmed as filtered, show NO companies at all —
                    // better than risking disclosure of other companies' messages.
                    // The flag is honored from both the top level and the legacy
                    // debug.* location; an absent flag still fails closed.
                    const filteringApplied = data.filteringApplied ?? data.debug?.filteringApplied
                    if (!this.isTO1User && companies.length > 0 && !filteringApplied) {
                        companies = []
                    }

                    // Defense in depth: when the backend company id is known, keep only
                    // the exact company_id match. A single non-exact result (matched by
                    // name on the server) is allowed; multiple non-matching results are
                    // discarded. Partial/substring matching is intentionally NOT used.
                    if (!this.isTO1User && companies.length > 0) {
                        const userCompanyId = authStore.company?.id?.toString()

                        if (userCompanyId) {
                            const exactMatches = companies.filter(
                                (company: any) => company.company_id === userCompanyId
                            )

                            if (exactMatches.length > 0) {
                                companies = exactMatches
                            } else if (companies.length > 1) {
                                companies = []
                            }
                        } else if (companies.length > 1) {
                            // Multiple companies but no way to verify ownership — show none
                            companies = []
                        }
                    }

                    this.companies = companies

                    // If no companies are available for the user, show a helpful error
                    if (this.companies.length === 0) {
                        if (!data.isTO1User) {
                            this.companiesError =
                                'Your company is not yet set up for messaging. Please contact support.'
                            console.error('No companies found for non-TO1 user. Debug info:', data.debug)
                        } else {
                            this.companiesError =
                                'No hotel companies found. Please check your connection or contact support.'
                        }
                    }

                    // Log debug information

                    // Auto-select company for specific user types
                    if (this.companies.length === 1 && !this.isTO1User) {
                        this.selectCompany(this.companies[0])
                    } else if (this.isSupplierUser && this.companies.length > 0) {
                        // For Supplier users, automatically select the first company since they can't see the companies list
                        this.selectCompany(this.companies[0])
                    }
                } else {
                    console.error('Invalid response format:', data)
                    throw new Error('Invalid response format from server')
                }
            } catch (error: any) {
                console.error('Error loading companies:', error)
                this.companiesError = error.message || 'Failed to load companies'
            } finally {
                this.isLoadingCompanies = false
            }
        }

        async selectCompany(company: Company) {
            this.selectedCompany = company
            this.selectedTicket = null

            // Store the company's internal ID in localStorage for use in ticket creation
            // This ensures we use the correct company ID for auto-replies
            if (company && company.id) {
                try {
                    const token = useCookie('token').value
                    if (!token) throw new Error('Authentication token not found')

                    // First try to use the ID directly (it might already be the internal ID)
                    try {
                        const response = await fetch(`/proxy/intercom/companies/${company.id}`, {
                            headers: {
                                Accept: 'application/json',
                                Token: token,
                            },
                        })

                        if (response.ok) {
                            // The ID is already an internal ID
                            localStorage.setItem(`company_${company.id}_internalId`, company.id)
                            // Persist to MongoDB (fire-and-forget)
                            fetch('/proxy/messaging/config', {
                                method: 'POST',
                                headers: {'Content-Type': 'application/json', Token: token},
                                body: JSON.stringify({key: `company_${company.id}_internalId`, value: company.id}),
                            }).catch(() => {})
                        } else {
                            throw new Error('Need to look up internal ID')
                        }
                    } catch (error) {
                        // If direct access fails, try to look up the internal ID

                        const searchResponse = await fetch(
                            `/proxy/intercom/companies/search?company_id=${company.id}`,
                            {
                                headers: {
                                    Accept: 'application/json',
                                    Token: token,
                                },
                            }
                        )

                        if (searchResponse.ok) {
                            const data = await searchResponse.json()
                            if (data && data.companies && data.companies.length > 0) {
                                const internalId = data.companies[0].id
                                localStorage.setItem(`company_${company.id}_internalId`, internalId)
                                // Persist to MongoDB (fire-and-forget)
                                fetch('/proxy/messaging/config', {
                                    method: 'POST',
                                    headers: {'Content-Type': 'application/json', Token: token},
                                    body: JSON.stringify({key: `company_${company.id}_internalId`, value: internalId}),
                                }).catch(() => {})
                            }
                        }
                    }
                } catch (error) {
                    console.error('Error storing company internal ID:', error)
                }
            }

            // Load tickets for the selected company
            this.loadTickets(company.id)
        }

        async loadTickets(companyId: string) {
            this.isLoadingTickets = true
            this.ticketsError = null
            this.tickets = []
            this.hasCompanyRespondents = false
            this.loadingProgress = ''

            try {
                const token = useCookie('token').value

                if (!token) {
                    throw new Error('Authentication token not found. Please log in.')
                }


                // Step 1: Get filtered company contacts (excluding those with role = "lead")
                this.loadingProgress = 'Loading company contacts...'
                const contactsUrl = `/proxy/intercom/companies/${companyId}/contacts-filtered`
                const contactsResponse = await fetch(contactsUrl, {
                    headers: {
                        Accept: 'application/json',
                        Token: token,
                    },
                })

                if (!contactsResponse.ok) {
                    throw new Error(
                        `Error getting contacts: ${contactsResponse.status} - ${contactsResponse.statusText}`
                    )
                }

                const contactsData = await contactsResponse.json()

                if (!contactsData.success) {
                    throw new Error('Failed to retrieve company contacts')
                }

                const contacts = contactsData.contacts || []
                const contactIds = contacts.map((contact: any) => contact.id)
                this.hasCompanyRespondents = contactIds.length > 0


                if (contactIds.length === 0) {
                    this.ticketsError = 'No contacts found for this company that can have tickets'
                    this.isLoadingTickets = false
                    return
                }

                // Log the contact IDs for debugging
                contactIds.forEach((id, index) => {
                })

                // Step 2: Get tickets for these contacts
                this.loadingProgress = 'Searching tickets...'

                // Prepare ticket type IDs for filtering
                const stringTicketTypeIds = this.hotelierTicketTypeIds.map(id => String(id))
                const ticketTypeIds = stringTicketTypeIds.length > 0 ? stringTicketTypeIds.join(',') : ''


                // Get the Zero Star Hotels company ID from localStorage if available
                const zeroStarHotelsCompanyId = localStorage.getItem('zeroStarHotelsCompanyId')

                // Get the target_company_id attribute ID for fast search
                // Read from localStorage directly since the prop may not be populated yet due to timing
                const questionsTypeId = localStorage.getItem('questionsTicketTypeId') || ''
                const targetCompanyAttrId = questionsTypeId
                    ? localStorage.getItem(`targetCompanyAttrId_${questionsTypeId}`) || ''
                    : ''

                // Construct the URL with the ticket type IDs, filtered contact IDs, and Zero Star Hotels company ID
                let url = ''
                if (ticketTypeIds) {
                    url = `/proxy/tickets?companyId=${companyId}&ticketTypeIds=${ticketTypeIds}&contactIds=${contactIds.join(',')}&zeroStarHotelsCompanyId=${zeroStarHotelsCompanyId || ''}&targetCompanyAttrId=${targetCompanyAttrId}`
                } else {
                    url = `/proxy/tickets?companyId=${companyId}&ticketTypeFilter=hotellier&contactIds=${contactIds.join(',')}&zeroStarHotelsCompanyId=${zeroStarHotelsCompanyId || ''}&targetCompanyAttrId=${targetCompanyAttrId}`
                }


                const response = await fetch(url, {
                    headers: {
                        Accept: 'application/json',
                        Token: token,
                    },
                })

                if (!response.ok) {
                    throw new Error(`Error: ${response.status} - ${response.statusText}`)
                }

                const data = await response.json()


                this.tickets = data.tickets || []
                this.loadingProgress = this.tickets.length > 0
                    ? `Found ${this.tickets.length} tickets, loading messages...`
                    : ''

                if (this.tickets.length > 0) {
                    if (this.tickets.length > 1) {
                    }
                }

                // Fetch messages for all tickets to determine last message sender
                await this.fetchMessagesForAllTickets()

            } catch (error: any) {
                console.error('Error loading tickets:', error)
                this.ticketsError = error.message || 'Failed to load tickets'
            } finally {
                this.isLoadingTickets = false
            }
        }

        async fetchMessagesForAllTickets() {
            if (!this.tickets.length) return


            const token = useCookie('token').value
            if (!token) return

            // Fetch messages for each ticket in parallel
            const messagePromises = this.tickets.map(async ticket => {
                try {
                    const response = await fetch(`/proxy/tickets/${ticket.id}/messages`, {
                        headers: {
                            Accept: 'application/json',
                            Token: token,
                        },
                    })

                    if (!response.ok) {
                        console.warn(`Failed to fetch messages for ticket ${ticket.id}`)
                        return
                    }

                    const data = await response.json()
                    const messages = data.messages || []
                    const ticketAuthorId = data.ticketAuthorId

                    // Process messages similar to TicketChat component
                    const processedMessages: Message[] = messages.map((msg: any) => {
                        const isAuthorMessage = ticketAuthorId && msg.senderId === ticketAuthorId
                        return {
                            id: msg.id,
                            text: maskSupportNameInText(msg.content),
                            sender: maskSupportName(msg.senderName),
                            timestamp: msg.createdAt,
                            isFromUser: msg.senderId === authStore.person?.personId,
                            senderId: msg.senderId,
                            senderExternalId: msg.senderExternalId,
                            isTicketAuthor: isAuthorMessage,
                        }
                    })

                    // Cache messages for instant display
                    ticket.cachedMessages = processedMessages

                    // Determine last message and sender role
                    if (processedMessages.length > 0) {
                        const lastMessage = processedMessages[processedMessages.length - 1]
                        const senderRole = await this.getUserRoleFromExternalId(
                            lastMessage.senderExternalId,
                            lastMessage.senderId,
                            lastMessage.sender
                        )
                        const isTO1Role = this.isUserTO1Role(senderRole)


                        ticket.lastMessage = {
                            senderId: lastMessage.senderId,
                            senderName: lastMessage.sender,
                            isTO1Role: isTO1Role,
                            timestamp: lastMessage.timestamp,
                            senderExternalId: lastMessage.senderExternalId,
                        }
                    }
                } catch (error) {
                    console.warn(`Error fetching messages for ticket ${ticket.id}:`, error)
                }
            })

            await Promise.all(messagePromises)
        }

        // Helper method to determine user role from external ID
        async getUserRoleFromExternalId(externalId?: string, senderId?: string, senderName?: string): Promise<string> {
            // If it's the current user, use their role
            // Check both externalId and senderId to match current user
            const currentUserId = authStore.person?.personId
            if (
                currentUserId &&
                (externalId === currentUserId.toString() ||
                    senderId === currentUserId.toString() ||
                    senderId === currentUserId)
            ) {
                return authStore.person?.role || ''
            }

            // If we have an external ID, try to get the role from the API
            if (externalId) {
                // Check cache first
                if (this.userRoleCache.has(externalId)) {
                    return this.userRoleCache.get(externalId)!
                }

                try {
                    const token = useCookie('token').value
                    if (token) {
                        const response = await fetch(`/proxy/users/get-user-role?user_id=${externalId}`, {
                            headers: {
                                Accept: 'application/json',
                                Token: token,
                            },
                        })

                        if (response.ok) {
                            const data = await response.json()
                            const role = data.role || 'unknown'
                            // Cache the result
                            this.userRoleCache.set(externalId, role)
                            return role
                        }
                    }
                } catch (error) {
                    console.warn(`Failed to fetch role for external ID ${externalId}:`, error)
                }
            }

            // Fallback to heuristics based on sender ID and name
            return this.getUserRoleFromHeuristics(senderId, senderName)
        }

        // Fallback method using heuristics
        getUserRoleFromHeuristics(senderId?: string, senderName?: string): string {
            // Check sender name for role indicators (case insensitive)
            const nameToCheck = (senderName || '').toLowerCase()
            if (
                nameToCheck.includes('director') ||
                nameToCheck.includes('supervisor') ||
                nameToCheck.includes('support') ||
                nameToCheck.includes('admin') ||
                nameToCheck.includes('to1') ||
                nameToCheck.includes('manager')
            ) {
                return 'support' // Treat as TO1 role
            }

            // For admin/support users, they typically have 'admin' in their sender ID
            if (
                senderId === 'admin' ||
                (senderId && senderId.includes('admin')) ||
                (senderId && senderId.includes('support'))
            ) {
                return 'support' // Treat admin/support as TO1 role
            }

            // Check if sender ID matches known TO1 user patterns
            if (
                senderId &&
                (senderId.includes('to1') ||
                    senderId.includes('TO1') ||
                    senderId.includes('director') ||
                    senderId.includes('supervisor'))
            ) {
                return 'support' // Treat as TO1 role
            }

            // Default to non-TO1 role for unknown senders
            return 'supplier'
        }

        selectTicket(ticket: Ticket) {
            this.selectedTicket = ticket
        }

        refreshTickets() {
            // Refresh tickets for the currently selected company
            if (this.selectedCompany) {
                this.loadTickets(this.selectedCompany.id)
            }
        }

        showNewTicketModal() {
            // Only TO1 users can create tickets
            if (!this.isTO1User) {
                this.showSnackbar = true
                this.snackbarText = 'Only TO1 users can create tickets'
                this.snackbarColor = 'warning'
                return
            }

            this.showTicketModal = true

            // Force a re-render by using nextTick
            this.$nextTick(() => {
            })
        }

        onTicketCreated(ticket: Ticket) {

            // Explicitly close the modal
            this.showTicketModal = false

            // Add the new ticket to the list
            this.tickets.unshift(ticket)

            // Select the new ticket
            this.selectTicket(ticket)

            // Show success notification
            this.snackbarText = 'Ticket created successfully'
            this.snackbarColor = 'success'
            this.showSnackbar = true
        }

        onTicketError(errorMessage: string) {
            // Show error notification
            this.snackbarText = errorMessage
            this.snackbarColor = 'error'
            this.showSnackbar = true
        }

        onTicketUpdated(ticket: Ticket) {
            // Update the ticket in the list
            const index = this.tickets.findIndex(t => t.id === ticket.id)
            if (index !== -1) {
                this.tickets[index] = ticket
            }
        }

        async onMessageSent(ticket: Ticket) {
            // When a message is sent, refresh the ticket data to update highlighting
            await this.fetchMessagesForAllTickets()
        }

        formatDate(timestamp: number): string {
            return format(new Date(timestamp), 'MMM d, yyyy h:mm a')
        }

        // Debug method to help diagnose modal issues
        debugModal() {

            // Toggle the modal state
            this.showTicketModal = !this.showTicketModal


            // Force a re-render
            this.$nextTick(() => {
            })
        }
    }
</script>

<style scoped>
    .gmail-layout {
        height: 100%;
        width: 100%;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        position: relative; /* Changed from absolute to relative */
    }

    /* Ensure the modal appears on top of other elements */
    :deep(.v-dialog) {
        z-index: 1000 !important;
    }

    .gmail-columns {
        display: flex;
        flex: 1 1 auto;
        width: 100%;
        gap: 16px; /* Add spacing between columns */
        padding: 16px; /* Add padding around the entire layout */
        min-height: 0; /* Crucial for flex items to respect parent container boundaries */
        overflow: hidden; /* Prevent overflow */
    }

    .gmail-column {
        height: 100%;
        overflow-y: auto; /* Enable vertical scrolling */
        overflow-x: hidden; /* Prevent horizontal scrolling */
        border-radius: 8px; /* Rounded corners for columns */
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05); /* Subtle shadow for depth */
        display: flex;
        flex-direction: column;
        min-height: 0; /* Crucial for flex items to respect parent container boundaries */
    }

    .companies-column {
        flex: 0 0 20%;
        min-width: 200px;
        margin-right: 4px; /* Additional spacing */
    }

    .tickets-column {
        flex: 0 0 30%;
        min-width: 250px;
        margin-right: 4px; /* Additional spacing */
    }

    .chat-column {
        flex: 1;
        min-width: 300px; /* Ensure minimum width */
        display: flex; /* Ensure proper display */
    }

    /* Supplier-specific layout adjustments */
    .supplier-layout .supplier-tickets-column {
        flex: 0 0 30%; /* Keep tickets column width intact at 30% */
        min-width: 250px;
        margin-right: 4px;
    }

    .supplier-layout .chat-column {
        flex: 1; /* Chat column takes remaining horizontal space */
        min-width: 300px;
        display: flex;
    }

    /* Mobile responsive adjustments */
    @media (max-width: 960px) {
        .gmail-columns {
            flex-direction: column;
            gap: 12px; /* Smaller gap for mobile */
            padding: 12px; /* Smaller padding for mobile */
        }

        .gmail-column {
            width: 100%;
            height: auto;
            margin-right: 0; /* Remove horizontal margins on mobile */
            margin-bottom: 12px; /* Add vertical spacing */
        }

        .companies-column,
        .tickets-column,
        .supplier-tickets-column {
            flex: 0 0 auto;
            max-height: 30vh;
        }

        .chat-column {
            flex: 1;
            margin-bottom: 0; /* Remove bottom margin from last item */
        }
    }
</style>
